import { FC, useState } from "react";
import { Spinner } from "@chakra-ui/react";
import { CheckCircleIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { useForm } from "react-hook-form";
import cx from "classnames";
import { formatFullName } from "../../../utils/functions";
import { EVENT_RESULT_SKIP_OPTIONS } from "../../../constants/config";
import { EVENT_RESULT_TYPES } from "../../../enums/eventresults";
import { useEndpoints } from "../../../hooks/useEndpoints";
import { useToastAlert } from "../../../hooks/useToastAlert/useToastAlert";
import { addBaseUrl } from "../../../lib/DurationPicker/utils";
import { IEvent } from "../../../models/event";
import { IEventResult } from "../../../models/eventResult";
import CustomAvatar from "../../../shared/CustomAvatar/CustomAvatar";
import CustomButton from "../../../shared/CustomButton/CustomButton";
import CustomTextInput from "../../../shared/CustomInput/CustomTextInput";
import CustomSelectInput from "../../../shared/CustomSelectInput/CustomSelectInput";
import Typhography from "../../../shared/Typhography/Typhography";
import style from "./CompileEvent.module.scss";

export const CompileSystemTestEvent: FC<{
  event: IEvent;
  eventResults: IEventResult[];
  onClose: () => void;
}> = ({ event, eventResults, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const toastAlert = useToastAlert();
  const [statusUser, setStatusUser] = useState<
    Record<string, "success" | "error" | "loading">
  >({});
  const endpoints = useEndpoints();

  const testDef =
    typeof (event as any).testId === "object" ? (event as any).testId : null;
  const testIdValue = testDef?._id ?? (event as any).testId;

  const { watch, setValue } = useForm({
    defaultValues: {
      records: event.userIds.map((user) => {
        const existing = eventResults.find(
          (r) => r.userId === user._id,
        ) as any;
        return {
          user,
          _id: user._id,
          value: existing?.value ?? "",
          notes: existing?.notes ?? "",
          skipped: existing?.skipped,
        };
      }),
    },
  });

  const records = watch("records");

  const [search, setSearch] = useState("");

  const filteredIndices = records.reduce<number[]>((acc, record, i) => {
    const fullName = `${record.user.firstName} ${record.user.lastName}`.toLowerCase();
    if (!search || fullName.includes(search.toLowerCase())) acc.push(i);
    return acc;
  }, []);

  const onChangeItem = (i: number, key: string, value: any) => {
    setValue(`records.${i}.${key}` as any, value);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Filter out athletes with no value and no skip reason
      const validRecords = records.filter((r) => r.skipped || (r.value !== '' && r.value != null));
      const items = validRecords.map((r) => ({
        eventId: event._id,
        userId: r._id,
        type: EVENT_RESULT_TYPES.SYSTEM_TEST,
        testId: testIdValue,
        ...(r.skipped
          ? { skipped: r.skipped }
          : {
              value: Number(r.value),
              ...(testDef?.defaultUnit ? { unit: testDef.defaultUnit } : {}),
              ...(r.notes ? { notes: r.notes } : {}),
            }),
      }));

      // Separate existing (patch) from new (bulk)
      const toUpdate: { id: string; body: any; userId: string }[] = [];
      const toCreate: any[] = [];
      const createIndexMap: number[] = []; // maps bulk index -> records index

      items.forEach((item, i) => {
        const existing = eventResults.find((r) => r.userId === item.userId);
        if (existing) {
          toUpdate.push({ id: existing._id, body: item, userId: item.userId });
        } else {
          createIndexMap.push(i);
          toCreate.push(item);
        }
      });

      // Patch existing results
      const patchResults = await Promise.all(
        toUpdate.map(async ({ id, body, userId }) => {
          try {
            setStatusUser((prev) => ({ ...prev, [userId]: "loading" }));
            await endpoints.patchEventResult(id, body);
            setStatusUser((prev) => ({ ...prev, [userId]: "success" }));
            return true;
          } catch {
            setStatusUser((prev) => ({ ...prev, [userId]: "error" }));
            return false;
          }
        })
      );

      // Bulk create new results
      let bulkSuccess = true;
      if (toCreate.length > 0) {
        toCreate.forEach((item) => setStatusUser((prev) => ({ ...prev, [item.userId]: "loading" })));
        const { data: bulkResults } = await endpoints.postEventResultBulk(toCreate);
        bulkResults.forEach((result) => {
          const recordIdx = createIndexMap[result.index];
          const userId = records[recordIdx]._id;
          setStatusUser((prev) => ({ ...prev, [userId]: result.success ? "success" : "error" }));
          if (!result.success) bulkSuccess = false;
        });
      }

      const hasErrors = patchResults.some((r) => !r) || !bulkSuccess;
      if (hasErrors) {
        toastAlert({ message: "Alcuni risultati non sono stati salvati", type: "error" });
      } else {
        toastAlert({ message: "Compilazione salvata con successo" });
      }
      onClose();
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {testDef && (
        <div className={style.toolbar}>
          <Typhography size="bold-14">{testDef.name}</Typhography>
          {testDef.defaultUnit && (
            <Typhography size="regular-12" style={{ marginLeft: 8 }}>
              Unità: {testDef.defaultUnit}
            </Typhography>
          )}
        </div>
      )}
      <div className="d-flex flex-column flex-1 overflow-auto gap-2">
        <div className={style.searchBar}>
          <CustomTextInput
            placeholder="Cerca atleta..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {filteredIndices.map((i) => {
          const { user, value, notes, skipped } = records[i];
          return (
            <div key={user._id} className={style.rowVertical}>
              <div className="d-flex align-items-center gap-2">
                <CustomAvatar
                  name={`${user.firstName[0]}${user.lastName[0]}`}
                  src={addBaseUrl(1, user.image?.image)}
                />
                <div className={style.userInfo}>
                  <Typhography size="bold-14" className={style.userName}>
                    {formatFullName(user.firstName, user.lastName)}
                  </Typhography>
                  {statusUser[user._id] === "success" && (
                    <div className={cx(style.statusBadge, style.success)}>
                      <CheckCircleIcon size={12} weight="fill" />
                      <Typhography size="regular-12">Salvato</Typhography>
                    </div>
                  )}
                  {statusUser[user._id] === "error" && (
                    <div className={cx(style.statusBadge, style.error)}>
                      <WarningCircleIcon size={12} weight="fill" />
                      <Typhography size="regular-12">Errore</Typhography>
                    </div>
                  )}
                  {statusUser[user._id] === "loading" && <Spinner size="xs" />}
                </div>
                <div className="ms-auto d-flex gap-2 align-items-end">
                  <CustomTextInput
                    disabled={!!skipped}
                    label={`Valore${testDef?.defaultUnit ? ` (${testDef.defaultUnit})` : ""}`}
                    value={value}
                    type="number"
                    classNameInput="w-100"
                    onValueNumberChange={(v) =>
                      onChangeItem(i, "value", v.valueAsNumber)
                    }
                  />
                  <CustomTextInput
                    disabled={!!skipped}
                    label="Note"
                    value={notes}
                    classNameInput="w-100"
                    onChange={(e) => onChangeItem(i, "notes", e.target.value)}
                  />
                  <div style={{ minWidth: 200 }}>
                    <CustomSelectInput
                      label="Assenza"
                      placeholder="Nessuna assenza"
                      options={EVENT_RESULT_SKIP_OPTIONS}
                      value={skipped}
                      clearOption={{ label: "Nessuna", value: undefined }}
                      onChange={(v) => onChangeItem(i, "skipped", v)}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className={style.actionsFooter}>
        <CustomButton color="outline" onClick={onClose} disabled={isLoading}>
          Annulla
        </CustomButton>
        <CustomButton
          color="secondary"
          isLoading={isLoading}
          disabled={records.some(
            (r) => !r.skipped && !r.value && r.value !== 0,
          )}
          onClick={handleSave}
        >
          Conferma
        </CustomButton>
      </div>
    </>
  );
};
